import { OccupationGroupCombobox } from '../shared/OccupationGroupCombobox';
import { MetroAreaCombobox } from '../shared/MetroAreaCombobox';
import type { Metro } from '../../types/metro';
import { OCCUPATIONAL_GROUPS } from '../../lib/occupational-groups';
import { useIsStuck } from '../../lib/use-is-stuck';
import { STICKY_OFFSET_BELOW_SECTION_NAV } from '../../lib/layout-offsets';
import { cn } from '../../lib/utils';
import { TableActions } from '../shared/TableActions';
import { RatioMethodologyNote } from '../rankings/RatioMethodologyNote';

interface DetailFilterBarProps {
  readonly occupationalGroupId: string;
  readonly onOccupationalGroupChange: (value: string) => void;
  /** Currently selected metro id. Empty string = no selection
   *  (page renders the empty state). Single-select per DEC-060. */
  readonly metroAreaId: string;
  readonly onMetroAreaIdChange: (id: string) => void;
  /**
   * Real metros from `useMetroData()`. Drives the Metro Area
   * combobox options. Pass `[]` while data is loading -- the
   * combobox degrades to an empty listbox / "No matches" panel,
   * which is harmless because the page renders LoadingState above
   * this bar in that state anyway.
   */
  readonly metros: ReadonlyArray<Metro>;
  /**
   * CSV serializer for the Copy / Export buttons in the embedded
   * <TableActions /> toolbar. Built by the page (MetroDetail) from
   * the currently-selected metro's occupation-group breakdown so
   * the export mirrors exactly what's on screen. Optional so the
   * filter bar still renders in isolation (e.g. for unit tests);
   * when omitted, the toolbar's existing "Nothing to copy" /
   * "Nothing to export" warn-toasts fire on click. See Slack
   * 2026-06-09 (Luis + Maria) -- the original wiring was missing
   * this prop entirely, which is why the buttons appeared broken.
   */
  readonly getCsvData?: () => string;
  /**
   * Per-OG annual credentials-awarded counts (IMP-018, Luis
   * 2026-06-09 ask -- mirrors IMP-017 on Providers). Pass-through
   * to the OG combobox -- each option renders a right-aligned
   * CountBadge showing `awardsSb` (annual credentials) for that OG
   * in the currently-selected metro.
   *
   * Keyed by the OG display NAME (matches `OccupationalGroup.name`),
   * not the chip id slug -- same convention as the Providers
   * surface so callers can pass the raw backend response through.
   *
   * Page sources this from `selectedMetro.occupationGroups` via a
   * useMemo in MetroDetail. `null` (or omitted) renders the
   * combobox without badges -- used for the no-metro empty state
   * and as a defensive default when the filter bar renders in
   * isolation (unit tests).
   *
   * Sparse-metro semantics: each metro carries 11-18 of the 22 OGs
   * in its data, so OGs missing from the map render without a
   * badge rather than as "(0)". A genuine zero (`awardsSb === 0`
   * with the OG present in the metro's data) does render a (0)
   * badge per Luis's spec -- the option stays clickable so the
   * empty bucket is a meaningful filter state, not a disabled row.
   */
  readonly ogCounts?: Readonly<Record<string, number>> | null;
}

/**
 * Sticky filter bar for the Metro Detail page. Two filters:
 * Metro Area (gating, leftmost, prominent) + Occupational Group
 * combobox (typeahead).
 *
 * Layout history:
 *   - Originally 4 controls: Search + OG select + Metro Area + Criteria
 *   - DEC-060: Criteria dropped (no source data; mirrors DEC-057)
 *   - IMP-002 (Maria 2026-06-01 Zoom 9:50 + Slack 14:30): Metro Area
 *     promoted from 3rd -> 1st position and given visually prominent
 *     treatment (wider min-width) because it is the gating selection:
 *     until the user picks one, no data renders.
 *   - IMP-003 (Mayuresh 2026-06-01 Zoom 10:30): the prior split
 *     "Search occupational groups..." input + OG select dropdown
 *     collapsed into a single APG typeahead combobox
 *     (`OccupationGroupCombobox`) -- one control for one intent.
 *   - IMP-007 (Mayuresh 2026-06-01 post-PR-72 feedback): the navy
 *     `ring-2 ring-navy` prominence treatment on the Metro slot
 *     was visually heavy and clashed with the rest of the bar.
 *     Dropped the ring + the "Required -- pick to load data"
 *     subtitle (which only made sense framed by the ring); the
 *     Metro slot kept its leftmost position. Swapped the plain
 *     BrandSelect for `MetroAreaCombobox` so the user can
 *     typeahead through 55 metros instead of scrolling.
 *   - 2026-06-05 width pass (Maria): Metro slot was still wider
 *     than the OG slot (`min-w-[320px] flex-1` vs `w-[280px]`)
 *     and visually dominated the bar. Width matched to OG
 *     (`w-[280px]`) so the two read as siblings.
 *
 * Pins to the top below Topbar + SectionNav (var(--offset-nav-stack),
 * a calc() built on the responsive var(--h-topbar) -- 134px = 74 + 60
 * desktop, CR-028/029) once the user scrolls past it (mobile: below
 * Topbar only, var(--h-topbar) resolving to 68px <600px since
 * SectionNav is hidden and var(--h-topbar) itself is the responsive
 * DEC-075 single-owner CSS var, TD-08a/b -- see BUG-002 fix in PR #73 /
 * RankingsFilterBar JSDoc for full root cause); elevation shadow
 * lights up only when stuck -- mirrors SectionNav's `is-stuck` treatment.
 *
 * Each filter wrapper carries a `data-testid="detail-filter-slot-*"`
 * so tests can assert visual order + per-slot styling without
 * scraping Tailwind class strings.
 */
export function DetailFilterBar({
  occupationalGroupId,
  onOccupationalGroupChange,
  metroAreaId,
  onMetroAreaIdChange,
  metros,
  getCsvData,
  ogCounts,
}: DetailFilterBarProps) {
  const [stickyRef, isStuck] = useIsStuck<HTMLDivElement>(STICKY_OFFSET_BELOW_SECTION_NAV);

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-[var(--h-topbar)] z-[99] min-[600px]:top-[var(--offset-nav-stack)] border-b border-border bg-ice transition-shadow',
        isStuck && 'shadow-sticky',
      )}
      role="region"
      aria-label="Metro detail filter bar"
    >
      <div className="mx-auto flex max-w-page flex-col gap-3 px-8 py-3 md:flex-row md:flex-wrap md:items-end md:gap-4">
        {/* IMP-002 + IMP-007 + 2026-06-05 width pass: Metro Area is
            the gating, primary selection. Leftmost position still
            carries the hierarchy on its own. The previous
            `min-w-[320px] flex-1` made the Metro slot visually
            dominate the bar -- it stretched to fill all remaining
            horizontal space and dwarfed the Occupational Group
            slot next to it. Width is now matched to the OG slot
            (280px) so the two read as siblings, not parent/child
            (Maria 2026-06-05). */}
        <div
          data-testid="detail-filter-slot-metro"
          className="md:w-filter-slot md:shrink-0"
        >
          <MetroAreaCombobox
            value={metroAreaId}
            onChange={onMetroAreaIdChange}
            metros={metros}
            placeholder="Search metro areas..."
          />
        </div>
        {/* IMP-003: single typeahead combobox replaces the prior
            split "Search occupational groups..." input + OG
            dropdown pair. */}
        <div
          data-testid="detail-filter-slot-og"
          className="md:w-filter-slot md:shrink-0"
        >
          <OccupationGroupCombobox
            value={occupationalGroupId}
            onChange={onOccupationalGroupChange}
            groups={OCCUPATIONAL_GROUPS}
            counts={ogCounts}
          />
        </div>
        {/* IMP-009 (Maria 2026-06-01 Zoom 9:23): ratio methodology
            pill — already on Home/Map/Rankings — surfaced here too so
            the affordance lives in a predictable spot across every
            data page. Sits between the filter controls and the
            right-aligned TableActions. */}
        <div className="md:self-end">
          <RatioMethodologyNote />
        </div>
        {/* Copy / Export / Print toolbar. Drops the previous
            `className=""` override so it picks up the default
            `md:ml-auto` and right-aligns inside the filter bar -- same
            position the Rankings page uses, so the affordance lives
            in a predictable spot across the data tabs (Maria 2026-06-05).
            getCsvData wired 2026-06-09 (Slack Luis + Maria) -- the
            page-level MetroDetail serializes the currently-selected
            metro's occupational-group breakdown into the same CSV
            schema as the visible <DetailTable />. Filename uses the
            metro slug (the metro id is the slug) when a metro is
            picked; falls back to a bare prefix in the empty state. */}
        <TableActions
          getCsvData={getCsvData}
          filename={metroAreaId ? `georgetown-cew-detail-${metroAreaId}` : 'georgetown-cew-detail'}
        />
      </div>
    </div>
  );
}
